import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import CopyButton from '../../shared/components/CopyButton';
import CredentialsView from '../../shared/components/CredentialsView';
import UserDetailModal from '../../shared/components/UserDetailModal';
import { createUser, getUsers, toggleUserStatus, updateUser, UserSortField } from './api';
import { generatePassword } from '../../shared/utils/password';
import { extractErrorMessage } from '../../shared/utils/errors';
import { PaginatedResponse, Role, User } from '../../shared/types';
import { getRoles, getAdminRoles, provisionAdmin } from '../roles/api';

interface IssuedCredentials {
  user: User;
  password: string;
  delivered?: { email?: boolean; sms?: boolean };
  context: 'created' | 'password_reset';
}

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<PaginatedResponse<User> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState<'' | 'user' | 'admin'>('');
  const [sortBy, setSortBy] = useState<UserSortField>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [profileUserId, setProfileUserId] = useState<number | null>(null);
  const [issued, setIssued] = useState<IssuedCredentials | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [backofficeRoles, setBackofficeRoles] = useState<Role[]>([]);
  const [selectedBackofficeRoleIds, setSelectedBackofficeRoleIds] = useState<number[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const loadBackofficeRoles = useCallback(async () => {
    setRolesError(null);
    try {
      const list = await getRoles();
      setBackofficeRoles(list);
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status === 403
            ? null
            : 'Failed to load backoffice roles'
          : 'Failed to load backoffice roles';
      if (msg) setRolesError(msg);
      setBackofficeRoles([]);
    }
  }, []);

  useEffect(() => {
    loadBackofficeRoles();
  }, [loadBackofficeRoles]);

  const toggleBackofficeRole = (id: number) => {
    setSelectedBackofficeRoleIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const loadUsers = useCallback(
    async (page: number) => {
      setIsLoading(true);
      try {
        const response = await getUsers({ page, role: roleFilter, sortBy, sortDir, search });
        setUsers(response);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [roleFilter, sortBy, sortDir, search],
  );

  useEffect(() => {
    loadUsers(currentPage);
  }, [currentPage, loadUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleRoleFilterChange = (role: '' | 'user' | 'admin') => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  const handleSort = (field: UserSortField) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const sortIndicator = (field: UserSortField) =>
    sortBy === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      const generatedPassword = formData.password;
      const res = await createUser(formData);
      const createdUser = res.user;

      if (formData.role === 'admin' && createdUser) {
        try {
          await provisionAdmin({
            id: createdUser.id,
            email: formData.email,
            name: formData.name,
            password: generatedPassword,
            role_ids: selectedBackofficeRoleIds,
          });
        } catch (provisionErr) {
          console.error('Failed to provision backoffice admin record:', provisionErr);
        }
      }

      setIsCreateModalOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'user' });
      setSelectedBackofficeRoleIds([]);
      setIssued({
        user: createdUser,
        password: generatedPassword,
        delivered: res.credentials_delivered,
        context: 'created',
      });
      loadUsers(currentPage);
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'Failed to create user'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setFormError('');
    setIsSubmitting(true);

    try {
      const updateData: { name?: string; role?: string; password?: string } = {
        name: formData.name,
        role: formData.role,
      };
      const issuedPassword = formData.password;
      if (issuedPassword) {
        updateData.password = issuedPassword;
      }

      const res = await updateUser(selectedUser.id, updateData);
      const editedUser = selectedUser;

      if (formData.role === 'admin') {
        try {
          await provisionAdmin({
            id: editedUser.id,
            email: formData.email || editedUser.email,
            name: formData.name || editedUser.name,
            password: issuedPassword || undefined,
            role_ids: selectedBackofficeRoleIds,
          });
        } catch (provisionErr) {
          console.error('Failed to sync backoffice admin record:', provisionErr);
        }
      }

      setIsEditModalOpen(false);
      setSelectedUser(null);
      setSelectedBackofficeRoleIds([]);

      if (issuedPassword) {
        setIssued({
          user: res.user ?? editedUser,
          password: issuedPassword,
          delivered: res.credentials_delivered,
          context: 'password_reset',
        });
      }
      loadUsers(currentPage);
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'Failed to update user'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      await toggleUserStatus(user.id, !user.is_active);
      loadUsers(currentPage);
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const openCreateModal = () => {
    setFormData({ name: '', email: '', password: generatePassword(14), role: 'user' });
    setSelectedBackofficeRoleIds([]);
    setFormError('');
    setIsCreateModalOpen(true);
  };

  const openEditModal = async (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
    });
    setSelectedBackofficeRoleIds([]);
    setFormError('');
    setIsEditModalOpen(true);

    if (user.role === 'admin') {
      try {
        const info = await getAdminRoles(user.id);
        setSelectedBackofficeRoleIds(info.roles.map((r) => r.id));
      } catch (err) {
        // user may not yet have an admin record — no roles to preload
      }
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Users</h1>
            <p className="text-slate-400 mt-1">Manage platform users</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input w-64"
              placeholder="Search by name, email or ID..."
              aria-label="Search users"
            />
            <select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value as '' | 'user' | 'admin')}
              className="input w-40"
              aria-label="Filter by role"
            >
              <option value="">All roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <button onClick={openCreateModal} className="btn btn-primary">
              + Add User
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th
                        className="table-header cursor-pointer select-none hover:text-white"
                        onClick={() => handleSort('id')}
                      >
                        ID{sortIndicator('id')}
                      </th>
                      <th
                        className="table-header cursor-pointer select-none hover:text-white"
                        onClick={() => handleSort('name')}
                      >
                        User{sortIndicator('name')}
                      </th>
                      <th
                        className="table-header cursor-pointer select-none hover:text-white"
                        onClick={() => handleSort('role')}
                      >
                        Role{sortIndicator('role')}
                      </th>
                      <th
                        className="table-header cursor-pointer select-none hover:text-white"
                        onClick={() => handleSort('is_active')}
                      >
                        Status{sortIndicator('is_active')}
                      </th>
                      <th
                        className="table-header cursor-pointer select-none hover:text-white"
                        onClick={() => handleSort('created_at')}
                      >
                        Created{sortIndicator('created_at')}
                      </th>
                      <th className="table-header text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {users?.data.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-800/30 transition-colors">
                        <td className="table-cell text-slate-400 font-mono">{user.id}</td>
                        <td className="table-cell">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white font-medium">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-white">{user.name}</p>
                              <p className="text-xs text-slate-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span
                            className={`badge ${
                              user.role === 'admin' ? 'badge-info' : 'badge-warning'
                            }`}
                          >
                            {user.role}
                          </span>
                        </td>
                        <td className="table-cell">
                          <button
                            onClick={() => handleToggleStatus(user)}
                            className={`badge cursor-pointer ${
                              user.is_active ? 'badge-success' : 'badge-danger'
                            }`}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="table-cell">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="table-cell text-right">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            <button
                              onClick={() => setProfileUserId(user.id)}
                              className="px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                            >
                              Profile
                            </button>
                            <button
                              onClick={() =>
                                navigate(`/user-activities?userId=${user.id}&type=user.login`)
                              }
                              className="px-3 py-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-all"
                            >
                              Sessions
                            </button>
                            <button
                              onClick={() => openEditModal(user)}
                              className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {users && users.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={users.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create New User"
      >
        <form onSubmit={handleCreateSubmit} className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input font-mono"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, password: generatePassword(14) })}
                className="px-3 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                title="Generate strong password"
              >
                Generate
              </button>
              <CopyButton value={formData.password} />
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Credentials will be emailed and texted to the user after creation.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Account type
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="input"
            >
              <option value="user">User — regular platform user</option>
              <option value="admin">Admin — backoffice operator</option>
            </select>
            <p className="mt-1 text-xs text-slate-500">
              &quot;Admin&quot; unlocks backoffice access. Specific permissions are granted via the
              backoffice roles below.
            </p>
          </div>

          <div className={formData.role === 'admin' ? '' : 'opacity-60'}>
            <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center justify-between">
              <span>Backoffice roles</span>
              {formData.role !== 'admin' && (
                <span className="text-xs font-normal text-amber-300/80">
                  Set account type to Admin to enable
                </span>
              )}
            </label>
            {rolesError && (
              <p className="text-xs text-red-400 mb-2">{rolesError}</p>
            )}
            {backofficeRoles.length === 0 ? (
              <p className="text-xs text-slate-500">
                No backoffice roles available. Create one in System &rarr; Roles first.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-2 border border-slate-700/40 rounded-lg p-2">
                {backofficeRoles.map((r) => {
                  const disabled = formData.role !== 'admin';
                  return (
                    <label
                      key={r.id}
                      className={`flex items-center gap-2 p-1.5 rounded transition-colors ${
                        disabled
                          ? 'cursor-not-allowed'
                          : selectedBackofficeRoleIds.includes(r.id)
                            ? 'bg-indigo-500/10 cursor-pointer'
                            : 'hover:bg-slate-800/40 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBackofficeRoleIds.includes(r.id)}
                        onChange={() => toggleBackofficeRole(r.id)}
                        disabled={disabled}
                        className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/30 disabled:cursor-not-allowed"
                      />
                      <span className="text-sm text-slate-200">{r.name}</span>
                      {r.is_system && (
                        <span className="badge badge-info text-[10px]">system</span>
                      )}
                      <span className="text-xs text-slate-500 ml-auto">
                        {r.permissions_count ?? 0} perms
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit User"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          {formError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              className="input opacity-50"
              disabled
            />
            <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              New Password <span className="text-slate-500">(optional)</span>
            </label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input font-mono"
                minLength={8}
                placeholder="Leave blank to keep current"
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, password: generatePassword(14) })}
                className="px-3 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                title="Generate strong password"
              >
                Generate
              </button>
              {formData.password && <CopyButton value={formData.password} />}
            </div>
            {formData.password && (
              <p className="mt-1 text-xs text-amber-300/80">
                The new password will be emailed and texted to the user.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Account type
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="input"
            >
              <option value="user">User — regular platform user</option>
              <option value="admin">Admin — backoffice operator</option>
            </select>
          </div>

          <div className={formData.role === 'admin' ? '' : 'opacity-60'}>
            <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center justify-between">
              <span>Backoffice roles</span>
              {formData.role !== 'admin' && (
                <span className="text-xs font-normal text-amber-300/80">
                  Set account type to Admin to enable
                </span>
              )}
            </label>
            {rolesError && (
              <p className="text-xs text-red-400 mb-2">{rolesError}</p>
            )}
            {backofficeRoles.length === 0 ? (
              <p className="text-xs text-slate-500">
                No backoffice roles available. Create one in System &rarr; Roles first.
              </p>
            ) : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-2 border border-slate-700/40 rounded-lg p-2">
                {backofficeRoles.map((r) => {
                  const disabled = formData.role !== 'admin';
                  return (
                    <label
                      key={r.id}
                      className={`flex items-center gap-2 p-1.5 rounded transition-colors ${
                        disabled
                          ? 'cursor-not-allowed'
                          : selectedBackofficeRoleIds.includes(r.id)
                            ? 'bg-indigo-500/10 cursor-pointer'
                            : 'hover:bg-slate-800/40 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBackofficeRoleIds.includes(r.id)}
                        onChange={() => toggleBackofficeRole(r.id)}
                        disabled={disabled}
                        className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/30 disabled:cursor-not-allowed"
                      />
                      <span className="text-sm text-slate-200">{r.name}</span>
                      {r.is_system && (
                        <span className="badge badge-info text-[10px]">system</span>
                      )}
                      <span className="text-xs text-slate-500 ml-auto">
                        {r.permissions_count ?? 0} perms
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setIsEditModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={issued !== null}
        onClose={() => setIssued(null)}
        title={
          issued?.context === 'password_reset' ? 'Password reset issued' : 'User credentials'
        }
        size="xl"
      >
        {issued && (
          <CredentialsView
            description={
              issued.context === 'password_reset'
                ? 'A new password has been set for this user. Keep this window open until you copy the credentials.'
                : 'New user account has been created. Share these credentials with the user securely.'
            }
            fields={[
              { label: 'Name', value: issued.user.name },
              { label: 'Email', value: issued.user.email },
              ...(issued.user.username
                ? [{ label: 'Username', value: issued.user.username }]
                : []),
              { label: 'Password', value: issued.password, mono: true },
              { label: 'Role', value: issued.user.role },
            ]}
            delivered={issued.delivered}
            notice="This password is shown only once. After closing this window it cannot be recovered."
          />
        )}
      </Modal>

      <UserDetailModal
        isOpen={profileUserId !== null}
        onClose={() => setProfileUserId(null)}
        userId={profileUserId}
      />
    </Layout>
  );
};

export default Users;
